import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useUser, UserButton } from '@clerk/clerk-react';
import { Plus, FileText, Calendar, DollarSign, Edit2, Trash2, TrendingUp, Loader2, RefreshCw, User, Mail, File, Send } from 'lucide-react';
import { createPortalSession, createCheckoutSession, getSubscriptionStatus } from '../utils/subscriptionClient';

const API_URL = process.env.REACT_APP_API_URL || 'https://tool.estimateassistant.com';

const Dashboard = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  
  const [estimates, setEstimates] = useState([]);
  const [statusDropdownFor, setStatusDropdownFor] = useState(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch estimates on mount
  useEffect(() => {
    fetchEstimates();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (statusDropdownFor === null) return;
    const close = () => setStatusDropdownFor(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [statusDropdownFor]);

  // Refresh estimates when returning to dashboard
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchEstimates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Load Plus Jakarta Sans font (matches the estimator's typography).
  // Idempotent — won't add the link twice if it's already in the head.
  useEffect(() => {
    if (!document.querySelector('link[href*="Plus+Jakarta+Sans"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, []);

  // Inject dashboard list/row styles. Uses a real stylesheet so we can use
  // media queries for responsive layout — inline styles don't support @media.
  useEffect(() => {
    const styleId = 'ea-dashboard-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .ea-list-header {
        display: grid;
        grid-template-columns: 1fr 110px 130px 140px;
        gap: 12px;
        align-items: center;
        padding: 8px 16px;
        background: #F8FAFC;
        border-bottom: 1px solid #DDE3EE;
        font-size: 10px;
        color: #5A6A84;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .ea-row {
        display: grid;
        grid-template-columns: 1fr 110px 130px 140px;
        gap: 12px;
        align-items: center;
        padding: 11px 16px;
        border-bottom: 1px solid #EAEEF5;
        cursor: pointer;
        transition: background 0.15s;
      }
      .ea-row:hover { background: #F8FAFC; }
      .ea-row:last-child { border-bottom: none; }
      .ea-cell-project { display: flex; align-items: center; gap: 10px; min-width: 0; }
      .ea-text { min-width: 0; flex: 1; }
      .ea-title-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .ea-title { font-size: 14px; font-weight: 700; color: #141C2E; }
      .ea-meta { font-size: 11px; color: #5A6A84; margin-top: 2px; }
      .ea-meta-mobile-date { display: none; }
      .ea-cell-updated { font-size: 12px; color: #5A6A84; }
      .ea-cell-total { text-align: right; font-size: 14px; font-weight: 700; color: #141C2E; letter-spacing: -0.01em; }
      .ea-cell-actions { display: flex; gap: 4px; justify-content: flex-end; }
      .ea-icon-btn {
        width: 30px; height: 30px;
        border: 1px solid #DDE3EE;
        background: white;
        border-radius: 5px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.15s;
        padding: 0;
        flex-shrink: 0;
        font-family: inherit;
      }
      .ea-icon-btn:hover { background: #F1F5F9; }
      .ea-icon-btn.ea-danger:hover { background: #FEE2E2; border-color: #FCA5A5; }

      @media (max-width: 640px) {
        .ea-list-header { display: none; }
        .ea-row {
          grid-template-columns: 1fr auto;
          grid-template-areas:
            "project total"
            "actions actions";
          row-gap: 8px;
          padding: 12px 14px;
        }
        .ea-cell-project { grid-area: project; }
        .ea-cell-total { grid-area: total; align-self: start; }
        .ea-cell-actions { grid-area: actions; justify-self: end; }
        .ea-cell-updated { display: none; }
        .ea-meta-mobile-date { display: inline; }

        /* Header tightening on mobile — hide brand text, shrink + button */
        .ea-brand-text { display: none !important; }
        .ea-new-btn-text { display: none !important; }
        .ea-new-btn { padding: 7px 10px !important; gap: 0 !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const fetchEstimates = async (isRefresh = false) => {
    if (!user) return;

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const token = await getToken();
      console.log('[Dashboard] Fetching estimates from:', `${API_URL}/api/v2/estimates`);

      const response = await fetch(`${API_URL}/api/v2/estimates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[Dashboard] Response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to fetch estimates');
      }

      const data = await response.json();
      console.log('[Dashboard] Received estimates:', data);
      console.log('[Dashboard] Estimates array:', data.estimates);
      console.log('[Dashboard] Number of estimates:', data.estimates?.length);
      if (data.estimates && data.estimates.length > 0) {
        console.log('[Dashboard] First estimate:', data.estimates[0]);
      }
      setEstimates(data.estimates || []);
      setError(null);
    } catch (err) {
      console.error('[Dashboard] Error fetching estimates:', err);
      setError('Failed to load estimates. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchEstimates(true);
  };

  // Change status optimistically — update the local UI immediately, then
  // call the backend. Revert if the call fails so the badge reflects truth.
  const handleStatusChange = async (estimateId, newStatus) => {
    setStatusDropdownFor(null); // close the dropdown

    // Capture the previous status in case we need to revert
    const previousEstimate = estimates.find(e => e.id === estimateId);
    if (!previousEstimate || previousEstimate.status === newStatus) return;

    // Optimistic update
    setEstimates(prev => prev.map(e =>
      e.id === estimateId
        ? { ...e, status: newStatus, status_updated_at: new Date().toISOString() }
        : e
    ));

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/v2/estimates/${estimateId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error(`Status update failed (${response.status})`);
      }

      // Backend may have a different status_updated_at than our optimistic guess —
      // sync with whatever it returned so the tooltip shows the right time
      const data = await response.json();
      if (data.estimate) {
        setEstimates(prev => prev.map(e =>
          e.id === estimateId ? { ...e, ...data.estimate } : e
        ));
      }
    } catch (err) {
      console.error('[Dashboard] Status update failed:', err);
      // Revert
      setEstimates(prev => prev.map(e =>
        e.id === estimateId ? previousEstimate : e
      ));
      alert('Failed to update status. Please try again.');
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/v2/estimates/${estimateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete estimate');
      }

      setEstimates(estimates.filter(est => est.id !== estimateId));
    } catch (err) {
      console.error('Error deleting estimate:', err);
      alert('Failed to delete estimate. Please try again.');
    }
  };

  const handleEditEstimate = (estimateId) => {
    window.location.href = '/contractor-estimate-builder-v2.html?id=' + estimateId;
  };

  const handleNewEstimate = () => {
    window.location.href = '/contractor-estimate-builder-v2.html';
  };

  const handleManageSubscription = async () => {
    try {
      const status = await getSubscriptionStatus(user.id);
      if (status.subscribed) {
        await createPortalSession(user);
      } else {
        await createCheckoutSession(user);
      }
    } catch (error) {
      console.error('Error opening subscription:', error);
      alert('Failed to open subscription management. Please try again.');
    }
  };

  const downloadPdf = async (estimateId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/v2/estimates/${estimateId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('PDF not found');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Could not load PDF. You may need to regenerate it.');
    }
  };

  const emailEstimate = async (estimate) => {
    // Check if PDF exists
    if (!estimate.pdf_generated_at) {
      alert('Please generate a PDF first before emailing. Open the estimate and create a PDF.');
      return;
    }

    const recipientEmail = prompt('Enter recipient email address:');
    if (!recipientEmail) return;

    // Basic email validation
    if (!recipientEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert('Please enter a valid email address.');
      return;
    }

    const recipientName = prompt('Enter recipient name (optional):') || 'Valued Client';
    const customMessage = prompt('Enter a custom message (optional):') || '';

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/v2/estimates/${estimate.id}/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: recipientEmail,
          recipientName,
          message: customMessage,
          subject: `Estimate: ${estimate.title || estimate.name || 'Your Project'}`
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email');
      }

      alert(`Email sent successfully to ${recipientEmail}!`);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email: ' + error.message);
    }
  };

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const monthlyEstimates = estimates.filter(est => {
    const dateValue = est.created_at || est.date;
    if (!dateValue) return false;
    const estDate = new Date(dateValue);
    if (isNaN(estDate.getTime())) return false;
    return estDate.getMonth() === currentMonth && estDate.getFullYear() === currentYear;
  });

  const monthlyTotal = monthlyEstimates.reduce((sum, est) => sum + parseFloat(est.total || 0), 0);
  const totalValue = estimates.reduce((sum, est) => sum + parseFloat(est.total || 0), 0);

  // Status filter: 'all' shows everything; any specific status filters to just those.
  // Treat null/missing status as 'draft' (matches the badge fallback elsewhere).
  const filteredEstimates = activeStatusFilter === 'all'
    ? estimates
    : estimates.filter(est => (est.status || 'draft') === activeStatusFilter);

  // Counts per status, used to show "(5)" badges on each tab.
  const statusCounts = estimates.reduce((acc, est) => {
    const s = est.status || 'draft';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Status badge config — colors picked to match the existing button palette
  // (blue/green/red already used for PDF/email/delete buttons elsewhere in this file)
  const STATUS_BADGE_STYLES = {
    draft:    { bg: '#f1f5f9', fg: '#64748b', label: 'Draft' },
    sent:     { bg: '#eff6ff', fg: '#2563eb', label: 'Sent' },
    pending:  { bg: '#fef3c7', fg: '#d97706', label: 'Pending' },
    approved: { bg: '#dcfce7', fg: '#16a34a', label: 'Approved' },
    declined: { bg: '#fee2e2', fg: '#dc2626', label: 'Declined' }
  };

  // Estimates created before this feature shipped may have null status —
  // treat them as drafts so the dashboard still renders something sensible.
  const renderStatusBadge = (status, statusUpdatedAt, estimateId) => {
    const config = STATUS_BADGE_STYLES[status] || STATUS_BADGE_STYLES.draft;
    const tooltipText = statusUpdatedAt
      ? `${config.label} · ${formatDate(statusUpdatedAt)} — click to change`
      : `${config.label} — click to change`;
    const isOpen = statusDropdownFor === estimateId;

    return (
      <span style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
        <span
          title={tooltipText}
          onClick={(e) => {
            e.stopPropagation();
            setStatusDropdownFor(isOpen ? null : estimateId);
          }}
          style={{
            display: 'inline-block',
            padding: '0.2rem 0.65rem',
            borderRadius: '9999px',
            backgroundColor: config.bg,
            color: config.fg,
            fontSize: '0.75rem',
            fontWeight: '600',
            letterSpacing: '0.025em',
            cursor: 'pointer',
            userSelect: 'none',
            border: isOpen ? `2px solid ${config.fg}` : '2px solid transparent',
            transition: 'border-color 0.15s'
          }}
        >
          {config.label}
        </span>

        {isOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '0.25rem',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              padding: '0.25rem',
              zIndex: 10,
              minWidth: '120px'
            }}
          >
            {Object.keys(STATUS_BADGE_STYLES).map(key => {
              const opt = STATUS_BADGE_STYLES[key];
              const selected = key === status;
              return (
                <div
                  key={key}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(estimateId, key);
                  }}
                  style={{
                    padding: '0.35rem 0.6rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    backgroundColor: selected ? opt.bg : 'transparent',
                    color: opt.fg,
                    fontWeight: selected ? '600' : '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: opt.fg,
                    display: 'inline-block'
                  }} />
                  {opt.label}
                </div>
              );
            })}
          </div>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} color="#1e3a5f" className="animate-spin" />
          <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#EEF1F6',
      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#141C2E'
    }}>
      <header style={{
        backgroundColor: '#141C2E',
        color: 'white',
        padding: '10px 22px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Left side: logo + product name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/Housecalclogo.png"
              alt="Estimate Assistant"
              style={{
                height: '44px',
                width: '44px',
                borderRadius: '6px',
                display: 'block'
              }}
            />
            <h1 className="ea-brand-text" style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: '700',
              letterSpacing: '-0.02em',
              color: 'white'
            }}>
              Estimate Assistant
            </h1>
          </div>

          {/* Right side: primary action + profile menu */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={handleNewEstimate}
              className="ea-new-btn"
              style={{
                backgroundColor: '#F59E0B',
                color: '#141C2E',
                border: '1px solid #F59E0B',
                padding: '7px 14px',
                borderRadius: '7px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontFamily: 'inherit'
              }}
            >
              <Plus size={16} strokeWidth={2.5} />
              <span className="ea-new-btn-text">New Estimate</span>
            </button>
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: "w-8 h-8"
                }
              }}
            >
              <UserButton.MenuItems>
                <UserButton.Link
                  label="Company Profile"
                  labelIcon={<User size={16} />}
                  href="/profile"
                />
                <UserButton.Action
                  label="Manage Subscription"
                  labelIcon={<DollarSign size={16} />}
                  onClick={handleManageSubscription}
                />
              </UserButton.MenuItems>
            </UserButton>
          </div>
        </div>
      </header>

      <main style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '2rem'
      }}>
        
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            color: '#991b1b'
          }}>
            {error}
          </div>
        )}

       <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '10px',
          marginBottom: '18px'
        }}>
          {/* This Month — count of estimates created in current month */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #DDE3EE',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{
                fontSize: '10px',
                color: '#5A6A84',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '2px'
              }}>
                This Month
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#141C2E',
                letterSpacing: '-0.02em'
              }}>
                {monthlyEstimates.length}
              </div>
            </div>
            <Calendar size={20} color="#2563EB" />
          </div>

          {/* Monthly Value — dollar value of current month's estimates */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #DDE3EE',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{
                fontSize: '10px',
                color: '#5A6A84',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '2px'
              }}>
                Monthly Value
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#141C2E',
                letterSpacing: '-0.02em'
              }}>
                {formatCurrency(monthlyTotal)}
              </div>
            </div>
            <TrendingUp size={20} color="#059669" />
          </div>

          {/* Portfolio Total — dollar value across all estimates */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #DDE3EE',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{
                fontSize: '10px',
                color: '#5A6A84',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '2px'
              }}>
                Portfolio Total
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#141C2E',
                letterSpacing: '-0.02em'
              }}>
                {formatCurrency(totalValue)}
              </div>
            </div>
            <div style={{
              fontSize: '11px',
              color: '#5A6A84',
              fontWeight: '600'
            }}>
              {estimates.length} total
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          {/* Section header: title + inline filter chips + refresh icon */}
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid #DDE3EE',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#141C2E',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Estimates
            </div>

            <div style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {estimates.length > 0 && [
                { key: 'all', label: 'All' },
                { key: 'draft', label: 'Draft' },
                { key: 'sent', label: 'Sent' },
                { key: 'pending', label: 'Pending' },
                { key: 'approved', label: 'Approved' },
                { key: 'declined', label: 'Declined' }
              ].map(tab => {
                const count = tab.key === 'all' ? estimates.length : (statusCounts[tab.key] || 0);
                const isActive = activeStatusFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveStatusFilter(tab.key)}
                    style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      borderRadius: '5px',
                      fontWeight: isActive ? 600 : 500,
                      border: 'none',
                      backgroundColor: isActive ? '#141C2E' : '#F1F5F9',
                      color: isActive ? 'white' : '#5A6A84',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    {tab.label}
                    <span style={{
                      color: isActive ? 'rgba(255,255,255,0.85)' : '#8FA0B8',
                      marginLeft: '2px'
                    }}>
                      {count}
                    </span>
                  </button>
                );
              })}

              {/* Refresh icon — small, subtle, at end of chip row */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh"
                style={{
                  marginLeft: '4px',
                  width: '28px',
                  height: '28px',
                  borderRadius: '5px',
                  border: '1px solid #DDE3EE',
                  backgroundColor: 'white',
                  cursor: refreshing ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#5A6A84',
                  opacity: refreshing ? 0.6 : 1,
                  fontFamily: 'inherit'
                }}
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          <div>
            {/* Column headers — desktop only */}
            {filteredEstimates.length > 0 && (
              <div className="ea-list-header">
                <div>Project</div>
                <div>Updated</div>
                <div style={{ textAlign: 'right' }}>Total</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>
            )}

            {filteredEstimates.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem 2rem',
                color: '#5A6A84'
              }}>
                <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                {estimates.length === 0 ? (
                  <>
                    <p style={{ margin: 0, fontSize: '1.1rem' }}>No estimates yet</p>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                      Create your first estimate to get started
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ margin: 0, fontSize: '1.1rem' }}>
                      No {activeStatusFilter} estimates
                    </p>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                      Try a different filter or create a new estimate
                    </p>
                  </>
                )}
              </div>
            ) : (
              filteredEstimates.map((estimate) => (
                  <div
                  className="ea-row"
                  key={estimate.id}
                  onClick={() => handleEditEstimate(estimate.id)}
                >
                  <div className="ea-cell-project">
                    <FileText size={16} color="#5A6A84" style={{ flexShrink: 0 }} />
                    <div className="ea-text">
                      <div className="ea-title-row">
                        <span className="ea-title">
                          {estimate.name || estimate.title || 'Untitled Estimate'}
                        </span>
                        {renderStatusBadge(estimate.status, estimate.status_updated_at, estimate.id)}
                      </div>
                      <div className="ea-meta">
                        <span className="ea-meta-mobile-date">
                          {formatDate(estimate.created_at || estimate.date)} ·{' '}
                        </span>
                        {estimate.item_count || 0} items
                      </div>
                    </div>
                  </div>
                  <div className="ea-cell-updated">
                    {formatDate(estimate.created_at || estimate.date)}
                  </div>
                  <div className="ea-cell-total">
                    {formatCurrency(estimate.total || 0)}
                  </div>
                  <div className="ea-cell-actions">
                    {estimate.pdf_generated_at && (
                      <>
                        <button
                          className="ea-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPdf(estimate.id);
                          }}
                          title={`PDF generated ${formatDate(estimate.pdf_generated_at)}`}
                        >
                          <File size={15} color="#2563EB" />
                        </button>
                        <button
                          className="ea-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            emailEstimate(estimate);
                          }}
                          title="Email estimate with PDF"
                        >
                          <Send size={15} color="#059669" />
                        </button>
                      </>
                    )}
                    <button
                      className="ea-icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEstimate(estimate.id);
                      }}
                      title="Edit"
                    >
                      <Edit2 size={15} color="#5A6A84" />
                    </button>
                    <button
                      className="ea-icon-btn ea-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEstimate(estimate.id);
                      }}
                      title="Delete"
                    >
                      <Trash2 size={15} color="#DC2626" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#1a202c',
        color: '#a0aec0',
        padding: '3rem 2rem',
        marginTop: '4rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <img
                  src="/Housecalclogo.png"
                  alt="Estimate Assistant"
                  style={{
                    height: '40px',
                    width: 'auto'
                  }}
                />
                <span style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: 'white'
                }}>
                  Estimate Assistant
                </span>
              </div>
              <p style={{
                fontSize: '0.875rem',
                lineHeight: '1.6',
                margin: 0
              }}>
                Professional contractor estimation made simple with AI-powered tools.
              </p>
            </div>

            <div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                marginBottom: '1rem',
                marginTop: 0
              }}>
                Contact
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <Mail size={16} />
                <a
                  href="mailto:support@estimateassistant.com"
                  style={{
                    color: '#a0aec0',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
                  onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
                >
                  support@estimateassistant.com
                </a>
              </div>
              <Link
                to="/contact"
                style={{
                  color: '#a0aec0',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
                onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
              >
                Contact Form →
              </Link>
            </div>

            <div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                marginBottom: '1rem',
                marginTop: 0
              }}>
                Legal
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <li>
                  <Link
                    to="/terms"
                    style={{
                      color: '#a0aec0',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
                    onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    style={{
                      color: '#a0aec0',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
                    onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid #2d3748',
            paddingTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem'
          }}>
            © 2025 Estimate Assistant. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;